import { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Progress } from '../components/ui/progress';
import { ScrollArea } from '../components/ui/scroll-area';
import {
  Upload,
  FileVideo,
  File,
  X,
  Check,
  Pause,
  Play,
  Trash2,
  LogOut,
  Clock,
  HardDrive,
  AlertCircle,
  RefreshCw,
  LayoutDashboard,
  Loader2,
  CheckCircle2,
  Settings,
  Ban,
  ArrowLeft,
  KeyRound
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const CHUNK_SIZE = 25 * 1024 * 1024; // 25MB chunks

const formatBytes = (bytes) => {
  if (bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};

const formatDate = (dateString) => {
  return new Date(dateString).toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

const formatDuration = (seconds) => {
  if (!seconds || seconds === Infinity) return '--:--';
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  return `${mins}:${secs.toString().padStart(2, '0')}`;
};

const UploadPortal = () => {
  const { user, token, logout, branding } = useAuth();
  const [uploads, setUploads] = useState([]);
  const [uploadQueue, setUploadQueue] = useState([]);
  const [incompleteUploads, setIncompleteUploads] = useState([]);
  const [isDragging, setIsDragging] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [logoError, setLogoError] = useState(false);
  const fileInputRef = useRef(null);
  const resumeFileInputRef = useRef(null);
  const resumeTargetRef = useRef(null);
  const uploadControllers = useRef({});
  const processingTimers = useRef({});
  const notifiedRejections = useRef(new Set());
  const isImpersonating = !!sessionStorage.getItem('admin_token');
  const [showPasswordDialog, setShowPasswordDialog] = useState(false);
  const [passwordForm, setPasswordForm] = useState({ current_password: '', new_password: '', confirm_password: '' });

  const returnToAdmin = () => {
    const adminToken = sessionStorage.getItem('admin_token');
    const adminUser = sessionStorage.getItem('admin_user');
    if (adminToken && adminUser) {
      localStorage.setItem('token', adminToken);
      localStorage.setItem('user', adminUser);
      sessionStorage.removeItem('admin_token');
      sessionStorage.removeItem('admin_user');
      window.location.href = '/admin/users';
    }
  };

  const handleChangePassword = async () => {
    if (passwordForm.new_password !== passwordForm.confirm_password) {
      toast.error('New passwords do not match');
      return;
    }
    try {
      await axios.post(`${API}/auth/change-password`, {
        current_password: passwordForm.current_password,
        new_password: passwordForm.new_password
      }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Password changed successfully');
      setShowPasswordDialog(false);
      setPasswordForm({ current_password: '', new_password: '', confirm_password: '' });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to change password');
    }
  };

  const logoUrl = branding?.logo_url && !logoError
    ? `${process.env.REACT_APP_BACKEND_URL}${branding.logo_url}`
    : null;

  const primaryColor = branding?.primary_color || '#DC2626';

  const fetchUploads = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/uploads`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUploads(response.data);
      
      // Check for new rejections and show popup
      response.data.forEach(u => {
        if (u.processing_status === 'rejected' && !notifiedRejections.current.has(u.id)) {
          notifiedRejections.current.add(u.id);
          toast.error(
            `REJECTED: ${u.filename}\n\n${u.rejection_reason || u.processing_message || 'No reason given'}`,
            { duration: 15000 }
          );
        }
      });
    } catch (error) {
      console.error('Failed to fetch uploads:', error);
      toast.error('Failed to load uploads');
    } finally {
      setIsLoading(false);
    }
  }, [token]);

  const fetchIncomplete = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/uploads/incomplete`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setIncompleteUploads(response.data);
    } catch (error) {
      console.error('Failed to fetch incomplete uploads:', error);
    }
  }, [token]);

  useEffect(() => {
    fetchUploads();
    fetchIncomplete();
  }, [fetchUploads, fetchIncomplete]);

  // Request desktop notification permission
  useEffect(() => {
    if ('Notification' in window && Notification.permission === 'default') {
      Notification.requestPermission();
    }
  }, []);

  // Auto-refresh when there are uploads being processed (in YOUR UPLOADS list)
  useEffect(() => {
    const hasProcessingUploads = uploads.some(u => 
      u.processing_status === 'validating' || 
      u.processing_status === 'converting' || 
      u.processing_status === 'pending' ||
      u.processing_status === 'conversion_queue' ||
      u.processing_status === 'assembling'
    );
    
    if (hasProcessingUploads) {
      const interval = setInterval(fetchUploads, 5000);
      return () => clearInterval(interval);
    }
  }, [uploads, fetchUploads]);

  // Cleanup processing timers on unmount
  useEffect(() => {
    const timers = processingTimers.current;
    return () => {
      Object.values(timers).forEach(clearInterval);
    };
  }, []);

  // Start polling processing status for a specific upload queue item
  const startProcessingPoll = (queueItemId, recordId, filename) => {
    // Clear any existing timer for this item
    if (processingTimers.current[queueItemId]) {
      clearInterval(processingTimers.current[queueItemId]);
    }

    const pollId = setInterval(async () => {
      try {
        const res = await axios.get(`${API}/uploads/${recordId}/processing`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        const { processing_status, processing_message } = res.data;

        if (processing_status === 'complete') {
          clearInterval(pollId);
          delete processingTimers.current[queueItemId];
          setUploadQueue(prev => prev.filter(f => f.id !== queueItemId));
          fetchUploads();
          toast.success(`${filename} is ready!`);
          // Desktop notification
          if (Notification.permission === 'granted') {
            new Notification('NOW Upload Portal', {
              body: `${filename} is ready!`,
              icon: logoUrl || '/favicon.ico'
            });
          }
        } else if (processing_status === 'failed') {
          clearInterval(pollId);
          delete processingTimers.current[queueItemId];
          setUploadQueue(prev => prev.map(f =>
            f.id === queueItemId ? { ...f, processingStatus: 'failed', processingMessage: processing_message } : f
          ));
          fetchUploads();
          // Desktop notification for failure
          if (Notification.permission === 'granted') {
            new Notification('NOW Upload Portal', {
              body: `${filename} processing failed`,
              icon: logoUrl || '/favicon.ico'
            });
          }
        } else {
          setUploadQueue(prev => prev.map(f =>
            f.id === queueItemId ? { ...f, processingStatus: processing_status, processingMessage: processing_message } : f
          ));
        }
      } catch (err) {
        console.error('Processing poll error:', err);
      }
    }, 4000);

    processingTimers.current[queueItemId] = pollId;
  };

  const MAX_CONCURRENT_UPLOADS = 2;

  const addToQueue = (files) => {
    const activeUploads = uploadQueue.filter(f => f.status === 'uploading' || f.status === 'pending');
    
    const fileArray = Array.from(files);
    const newItems = [];

    for (const file of fileArray) {
      // Check if this file matches an incomplete upload (by size)
      const match = incompleteUploads.find(inc => inc.file_size === file.size);
      if (match) {
        if (activeUploads.length >= MAX_CONCURRENT_UPLOADS) {
          toast.error('Please wait for a current upload to finish before resuming.');
          continue;
        }
        resumeIncompleteUpload(match, file);
        continue;
      }

      const fileItem = {
        id: `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        file,
        name: file.name,
        size: file.size,
        progress: 0,
        status: (activeUploads.length + newItems.length) < MAX_CONCURRENT_UPLOADS ? 'pending' : 'queued',
        uploadId: null,
        speed: 0,
        eta: null,
        uploadedChunks: 0,
        totalChunks: Math.ceil(file.size / CHUNK_SIZE)
      };

      newItems.push(fileItem);
    }

    if (newItems.length > 0) {
      setUploadQueue(prev => [...prev, ...newItems]);
      // Start uploads for items that aren't queued
      newItems.filter(f => f.status === 'pending').forEach(item => startUpload(item));
      const queuedCount = newItems.filter(f => f.status === 'queued').length;
      if (queuedCount > 0) {
        toast.info(`${queuedCount} file(s) queued. They'll start when current uploads finish.`);
      }
    }
  };

  const handleResumeClick = (incomplete) => {
    resumeTargetRef.current = incomplete;
    resumeFileInputRef.current?.click();
  };

  const handleResumeFileSelect = (e) => {
    const file = e.target.files?.[0];
    const target = resumeTargetRef.current;
    if (!file || !target) return;
    
    // Accept any file the user selects — they know which file it is
    resumeIncompleteUpload(target, file);
    e.target.value = '';
    resumeTargetRef.current = null;
  };

  const resumeIncompleteUpload = async (incomplete, file) => {
    const fileItem = {
      id: incomplete.id,
      file,
      name: incomplete.filename,
      size: incomplete.file_size,
      progress: incomplete.progress,
      status: 'uploading',
      uploadId: incomplete.id,
      speed: 0,
      eta: null,
      uploadedChunks: incomplete.received_chunks,
      totalChunks: incomplete.total_chunks
    };

    setUploadQueue(prev => [...prev, fileItem]);
    // Remove from incomplete list
    setIncompleteUploads(prev => prev.filter(inc => inc.id !== incomplete.id));

    const controller = new AbortController();
    uploadControllers.current[fileItem.id] = controller;

    // Helper: upload a single chunk with retries
    const uploadSingleChunk = async (idx) => {
      const MAX_RETRIES = 4;
      for (let attempt = 0; attempt < MAX_RETRIES; attempt++) {
        try {
          const start = idx * CHUNK_SIZE;
          const end = Math.min(start + CHUNK_SIZE, file.size);
          const chunk = file.slice(start, end);
          const formData = new FormData();
          formData.append('chunk', chunk);

          await axios.post(`${API}/uploads/${incomplete.id}/chunk/${idx}`, formData, {
            headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' },
            signal: controller.signal,
            timeout: 120000
          });
          return end - start;
        } catch (err) {
          if (err.name === 'CanceledError' || err.name === 'AbortError') throw err;
          if (attempt < MAX_RETRIES - 1) {
            const delay = Math.min(1000 * Math.pow(2, attempt), 8000);
            await new Promise(r => setTimeout(r, delay));
          } else {
            throw err;
          }
        }
      }
    };

    try {
      // Get the actual list of missing chunks from backend
      let chunksToUpload = [];
      try {
        const missingRes = await axios.get(`${API}/uploads/${incomplete.id}/missing-chunks`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        chunksToUpload = missingRes.data.missing_chunks || [];
      } catch (_) {
        // Fallback: upload from received_chunks count onwards
        for (let i = incomplete.received_chunks; i < incomplete.total_chunks; i++) {
          chunksToUpload.push(i);
        }
      }

      if (chunksToUpload.length === 0) {
        // All chunks already received — just complete
        toast.info('All chunks already uploaded, completing...');
      } else {
        const startTime = Date.now();
        let uploadedBytes = incomplete.received_chunks * CHUNK_SIZE;
        const PARALLEL_CHUNKS = 3;

        toast.success(`Resuming upload — ${chunksToUpload.length} chunks remaining`);

        for (let i = 0; i < chunksToUpload.length; i += PARALLEL_CHUNKS) {
          const batch = [];
          for (let j = 0; j < PARALLEL_CHUNKS && (i + j) < chunksToUpload.length; j++) {
            batch.push(uploadSingleChunk(chunksToUpload[i + j]));
          }
          const sizes = await Promise.all(batch);
          uploadedBytes += sizes.reduce((a, b) => a + b, 0);
          const progress = Math.round((uploadedBytes / file.size) * 100);
          const elapsedSeconds = (Date.now() - startTime) / 1000;
          const speed = sizes.reduce((a, b) => a + b, 0) * (1000 / Math.max(1, Date.now() - startTime)) || 0;
          const remainingBytes = file.size - uploadedBytes;
          const eta = speed > 0 ? remainingBytes / speed : 0;

          setUploadQueue(prev => prev.map(f => 
            f.id === fileItem.id ? { ...f, progress: Math.min(progress, 99), uploadedChunks: incomplete.received_chunks + i + batch.length, speed, eta } : f
          ));
        }
      }

      // Verify all chunks before completing
      try {
        const verifyRes = await axios.get(`${API}/uploads/${incomplete.id}/missing-chunks`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        const { missing_chunks } = verifyRes.data;
        if (missing_chunks && missing_chunks.length > 0) {
          console.warn(`Resume verify: ${missing_chunks.length} chunks still missing, retrying`);
          for (const idx of missing_chunks) {
            await uploadSingleChunk(idx);
          }
        }
      } catch (_) {}

      // Complete upload
      const completeRes = await axios.post(`${API}/uploads/${incomplete.id}/complete`, {}, {
        headers: { Authorization: `Bearer ${token}` },
        signal: controller.signal
      });

      const recordId = completeRes.data.upload_id;

      setUploadQueue(prev => prev.map(f => 
        f.id === fileItem.id ? { ...f, status: 'processing', progress: 100, recordId, processingStatus: 'pending' } : f
      ));

      toast.success(`${incomplete.filename} uploaded! Processing video...`);
      fetchIncomplete();
      startProcessingPoll(fileItem.id, recordId, incomplete.filename);

    } catch (error) {
      if (error.name === 'CanceledError' || error.name === 'AbortError') return;
      // Check if upload was actually created despite the error
      try {
        const check = await axios.get(`${API}/uploads`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        const created = check.data.find(u => u.filename === incomplete.filename &&
          ['pending', 'conversion_queue', 'validating', 'converting', 'complete', 'failed'].includes(u.processing_status));
        if (created) {
          setUploadQueue(prev => prev.map(f =>
            f.id === fileItem.id ? { ...f, status: 'processing', progress: 100, recordId: created.id, processingStatus: created.processing_status } : f
          ));
          toast.success(`${incomplete.filename} uploaded! Processing video...`);
          fetchIncomplete();
          startProcessingPoll(fileItem.id, created.id, incomplete.filename);
          return;
        }
      } catch (_) {}

      console.error('Resume failed:', error);
      setUploadQueue(prev => prev.map(f => 
        f.id === fileItem.id ? { ...f, status: 'failed' } : f
      ));
      toast.error(`Failed to resume ${incomplete.filename}`);
      fetchIncomplete();
    }
  };

  const cancelIncomplete = async (sessionId) => {
    try {
      await axios.delete(`${API}/uploads/incomplete/${sessionId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Incomplete upload cancelled');
      fetchIncomplete();
    } catch (error) {
      toast.error('Failed to cancel upload');
    }
  };

  const startUpload = async (fileItem) => {
    const controller = new AbortController();
    uploadControllers.current[fileItem.id] = controller;

    setUploadQueue(prev => prev.map(f => 
      f.id === fileItem.id ? { ...f, status: 'uploading' } : f
    ));

    try {
      // Initialize upload
      const initResponse = await axios.post(`${API}/uploads/init`, {
        filename: fileItem.name,
        file_size: fileItem.size,
        total_chunks: fileItem.totalChunks,
        content_type: fileItem.file.type
      }, {
        headers: { Authorization: `Bearer ${token}` },
        signal: controller.signal
      });

      const { upload_id } = initResponse.data;

      setUploadQueue(prev => prev.map(f => 
        f.id === fileItem.id ? { ...f, uploadId: upload_id } : f
      ));

      // Helper: upload a single chunk with retries
      const uploadSingleChunk = async (idx) => {
        const MAX_RETRIES = 4;
        for (let attempt = 0; attempt < MAX_RETRIES; attempt++) {
          try {
            const start = idx * CHUNK_SIZE;
            const end = Math.min(start + CHUNK_SIZE, fileItem.size);
            const chunk = fileItem.file.slice(start, end);
            const formData = new FormData();
            formData.append('chunk', chunk);

            await axios.post(`${API}/uploads/${upload_id}/chunk/${idx}`, formData, {
              headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' },
              signal: controller.signal,
              timeout: 120000 // 2 min per chunk
            });
            return end - start; // success — return bytes uploaded
          } catch (err) {
            if (err.name === 'CanceledError' || err.name === 'AbortError') throw err;
            if (attempt < MAX_RETRIES - 1) {
              const delay = Math.min(1000 * Math.pow(2, attempt), 8000);
              console.warn(`Chunk ${idx} attempt ${attempt + 1} failed, retrying in ${delay}ms...`);
              await new Promise(r => setTimeout(r, delay));
            } else {
              console.error(`Chunk ${idx} failed after ${MAX_RETRIES} attempts`);
              throw err;
            }
          }
        }
      };

      // Upload chunks with controlled parallelism + per-chunk retry
      const startTime = Date.now();
      let uploadedBytes = 0;
      const PARALLEL_CHUNKS = 3;

      for (let i = 0; i < fileItem.totalChunks; i += PARALLEL_CHUNKS) {
        const batch = [];
        for (let j = 0; j < PARALLEL_CHUNKS && (i + j) < fileItem.totalChunks; j++) {
          batch.push(uploadSingleChunk(i + j));
        }

        const sizes = await Promise.all(batch);
        uploadedBytes += sizes.reduce((a, b) => a + b, 0);
        const lastChunkIdx = Math.min(i + PARALLEL_CHUNKS, fileItem.totalChunks);
        const progress = Math.round((uploadedBytes / fileItem.size) * 100);
        const elapsedSeconds = (Date.now() - startTime) / 1000;
        const speed = uploadedBytes / elapsedSeconds;
        const remainingBytes = fileItem.size - uploadedBytes;
        const eta = remainingBytes / speed;

        setUploadQueue(prev => prev.map(f => 
          f.id === fileItem.id ? { 
            ...f, 
            progress, 
            uploadedChunks: lastChunkIdx,
            speed,
            eta
          } : f
        ));
      }

      // Verify all chunks received before completing
      try {
        const verifyRes = await axios.get(`${API}/uploads/${upload_id}/missing-chunks`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        const { missing_chunks } = verifyRes.data;
        if (missing_chunks && missing_chunks.length > 0) {
          console.warn(`Found ${missing_chunks.length} missing chunks, retrying:`, missing_chunks);
          for (const idx of missing_chunks) {
            await uploadSingleChunk(idx);
          }
        }
      } catch (verifyErr) {
        console.warn('Chunk verification check failed, proceeding:', verifyErr);
      }

      // Complete upload
      const completeRes = await axios.post(`${API}/uploads/${upload_id}/complete`, {}, {
        headers: { Authorization: `Bearer ${token}` },
        signal: controller.signal
      });

      const recordId = completeRes.data.upload_id;

      // Upload done — now enter processing state
      setUploadQueue(prev => prev.map(f => 
        f.id === fileItem.id ? { ...f, status: 'processing', progress: 100, recordId, processingStatus: 'pending' } : f
      ));

      toast.success(`${fileItem.name} uploaded! Processing video...`);

      // Start polling for processing status
      startProcessingPoll(fileItem.id, recordId, fileItem.name);

    } catch (error) {
      if (error.name === 'CanceledError' || error.name === 'AbortError') {
        return;
      }
      // The backend may have processed the upload even if the response was lost (timeout, network blip).
      // Check if the upload record was actually created.
      try {
        const check = await axios.get(`${API}/uploads`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        const created = check.data.find(u => u.filename === fileItem.name && 
          ['pending', 'conversion_queue', 'validating', 'converting', 'complete', 'failed'].includes(u.processing_status));
        if (created) {
          setUploadQueue(prev => prev.map(f => 
            f.id === fileItem.id ? { ...f, status: 'processing', progress: 100, recordId: created.id, processingStatus: created.processing_status } : f
          ));
          toast.success(`${fileItem.name} uploaded! Processing video...`);
          startProcessingPoll(fileItem.id, created.id, fileItem.name);
          return;
        }
      } catch (_) { /* ignore check failure */ }

      console.error('Upload failed:', error);
      setUploadQueue(prev => prev.map(f => 
        f.id === fileItem.id ? { ...f, status: 'failed' } : f
      ));
      toast.error(`Failed to upload ${fileItem.name}`);
    }
  };

  const pauseUpload = (fileId) => {
    const controller = uploadControllers.current[fileId];
    if (controller) {
      controller.abort();
    }
    setUploadQueue(prev => prev.map(f => 
      f.id === fileId ? { ...f, status: 'paused' } : f
    ));
  };

  const resumeUpload = (fileItem) => {
    startUpload(fileItem);
  };

  const cancelUpload = (fileId) => {
    const controller = uploadControllers.current[fileId];
    if (controller) {
      controller.abort();
    }
    setUploadQueue(prev => prev.filter(f => f.id !== fileId));
    delete uploadControllers.current[fileId];
  };

  // Process queued items when active uploads finish
  useEffect(() => {
    const active = uploadQueue.filter(f => f.status === 'uploading' || f.status === 'pending');
    const queued = uploadQueue.filter(f => f.status === 'queued');
    
    if (active.length < MAX_CONCURRENT_UPLOADS && queued.length > 0) {
      const slotsAvailable = MAX_CONCURRENT_UPLOADS - active.length;
      const toStart = queued.slice(0, slotsAvailable);
      
      setUploadQueue(prev => prev.map(f => 
        toStart.find(s => s.id === f.id) ? { ...f, status: 'pending' } : f
      ));
      toStart.forEach(item => startUpload(item));
    }
  }, [uploadQueue]); // eslint-disable-line react-hooks/exhaustive-deps

  const deleteUpload = async (uploadId) => {
    try {
      await axios.delete(`${API}/uploads/${uploadId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('File deleted');
      fetchUploads();
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to delete file';
      toast.error(message);
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    addToQueue(e.dataTransfer.files);
  };

  const handleFileSelect = (e) => {
    if (e.target.files) {
      addToQueue(e.target.files);
    }
  };

  const getFileIcon = (filename) => {
    const ext = filename.split('.').pop()?.toLowerCase();
    const videoExts = ['mp4', 'mov', 'avi', 'mkv', 'wmv', 'flv', 'webm', 'mxf', 'mts'];
    if (videoExts.includes(ext)) {
      return <FileVideo className="w-5 h-5 text-red-500" />;
    }
    return <File className="w-5 h-5 text-zinc-500" />;
  };

  const getStatusBadge = (status, processingStatus) => {
    if (status === 'processing') {
      if (processingStatus === 'converting') {
        return (
          <span className="flex items-center gap-1 px-2 py-1 text-xs font-medium uppercase bg-yellow-500/20 text-yellow-400 rounded">
            <Settings className="w-3 h-3 animate-spin" />
            Converting
          </span>
        );
      }
      if (processingStatus === 'failed') {
        return (
          <span className="flex items-center gap-1 px-2 py-1 text-xs font-medium uppercase bg-red-500/20 text-red-400 rounded">
            <AlertCircle className="w-3 h-3" />
            Failed
          </span>
        );
      }
      return (
        <span className="flex items-center gap-1 px-2 py-1 text-xs font-medium uppercase bg-blue-500/20 text-blue-400 rounded">
          <Loader2 className="w-3 h-3 animate-spin" />
          Processing
        </span>
      );
    }
    const styles = {
      completed: 'status-completed',
      uploading: 'status-uploading',
      failed: 'status-failed',
      pending: 'status-pending',
      paused: 'status-pending'
    };
    return (
      <span className={`px-2 py-1 text-xs font-medium uppercase ${styles[status] || styles.pending}`}>
        {status}
      </span>
    );
  };

  const getProcessingBadge = (processingStatus, processingMessage) => {
    if (!processingStatus || processingStatus === 'complete') {
      return (
        <span className="flex items-center gap-1 px-2 py-1 text-xs font-medium uppercase bg-green-500/20 text-green-400 rounded">
          <CheckCircle2 className="w-3 h-3" />
          Ready
        </span>
      );
    }
    
    if (processingStatus === 'validating') {
      return (
        <span className="flex items-center gap-1 px-2 py-1 text-xs font-medium uppercase bg-blue-500/20 text-blue-400 rounded">
          <Loader2 className="w-3 h-3 animate-spin" />
          Checking
        </span>
      );
    }
    
    if (processingStatus === 'converting') {
      return (
        <span className="flex items-center gap-1 px-2 py-1 text-xs font-medium uppercase bg-yellow-500/20 text-yellow-400 rounded" title={processingMessage}>
          <Settings className="w-3 h-3 animate-spin" />
          Converting
        </span>
      );
    }
    
    if (processingStatus === 'assembling') {
      return (
        <span className="flex items-center gap-1 px-2 py-1 text-xs font-medium uppercase bg-blue-500/20 text-blue-400 rounded">
          <Loader2 className="w-3 h-3 animate-spin" />
          Uploading
        </span>
      );
    }
    
    if (processingStatus === 'pending' || processingStatus === 'conversion_queue') {
      return (
        <span className="flex items-center gap-1 px-2 py-1 text-xs font-medium uppercase bg-orange-500/20 text-orange-400 rounded" title={processingMessage}>
          <Clock className="w-3 h-3" />
          In Queue
        </span>
      );
    }
    
    if (processingStatus === 'rejected') {
      return (
        <span className="flex items-center gap-1 px-2 py-1 text-xs font-medium uppercase bg-red-500/20 text-red-400 rounded" title={processingMessage}>
          <Ban className="w-3 h-3" />
          Rejected
        </span>
      );
    }
    
    if (processingStatus === 'failed') {
      return (
        <span className="flex items-center gap-1 px-2 py-1 text-xs font-medium uppercase bg-red-500/20 text-red-400 rounded" title={processingMessage}>
          <AlertCircle className="w-3 h-3" />
          Failed
        </span>
      );
    }
    
    return null;
  };

  const backgroundColor = branding?.background_color || '#09090B';

  return (
    <div className="min-h-screen" style={{ backgroundColor }} data-testid="upload-portal">
      {/* Impersonation Banner */}
      {isImpersonating && (
        <div className="bg-yellow-500 text-black py-2 px-4 flex items-center justify-between" data-testid="impersonation-banner">
          <span className="text-sm font-medium">Viewing as: <strong>{user?.username}</strong></span>
          <Button
            size="sm"
            variant="outline"
            onClick={returnToAdmin}
            className="bg-black text-yellow-500 border-black hover:bg-zinc-900 h-7 text-xs"
            data-testid="return-to-admin-btn"
          >
            <ArrowLeft className="w-3 h-3 mr-1" />
            Return to Admin
          </Button>
        </div>
      )}
      {/* Header */}
      <header className="sticky top-0 z-50 glass border-b border-white/10">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 h-14 sm:h-16 flex items-center justify-between">
          <div className="flex items-center gap-2 sm:gap-4">
            {logoUrl ? (
              <img 
                src={logoUrl}
                alt="Logo"
                className="h-8 sm:h-10 w-auto object-contain"
                onError={() => setLogoError(true)}
              />
            ) : (
              <div className="w-8 h-8 sm:w-9 sm:h-9 rounded-lg flex items-center justify-center" style={{ backgroundColor: primaryColor }}>
                <Upload className="w-4 h-4 text-white" />
              </div>
            )}
            <span className="font-bold text-sm sm:text-xl text-white tracking-wide uppercase hidden xs:inline">
              TV Program Upload Center
            </span>
            <span className="font-bold text-sm text-white tracking-wide uppercase xs:hidden">
              Upload Center
            </span>
          </div>
          
          <div className="flex items-center gap-2 sm:gap-6">
            {user?.role === 'admin' && (
              <Link
                to="/admin"
                className="flex items-center gap-1 sm:gap-2 text-zinc-400 hover:text-white transition-colors text-xs sm:text-sm font-medium"
                data-testid="back-to-dashboard"
              >
                <LayoutDashboard className="w-4 h-4" />
                <span className="hidden sm:inline">Dashboard</span>
              </Link>
            )}
            <span className="hidden sm:inline text-zinc-400 text-sm">
              Welcome, <span className="text-white font-medium">{user?.username}</span>
            </span>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setShowPasswordDialog(true)}
              className="text-zinc-400 hover:text-white hover:bg-zinc-800 rounded-lg px-2"
              title="Change Password"
              data-testid="change-password-btn"
            >
              <KeyRound className="w-4 h-4" />
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={logout}
              className="text-zinc-400 hover:text-white hover:bg-zinc-800 rounded-lg px-2 sm:px-3"
              data-testid="logout-btn"
            >
              <LogOut className="w-4 h-4 sm:mr-2" />
              <span className="hidden sm:inline">Sign Out</span>
            </Button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-6xl mx-auto px-4 sm:px-6 py-6 sm:py-12">
        {/* Important Notice - Upload Deadline */}
        <div className="mb-4 sm:mb-6 p-4 sm:p-6 rounded-xl sm:rounded-2xl text-center" style={{ backgroundColor: `${primaryColor}20`, borderColor: `${primaryColor}50`, borderWidth: '1px' }} data-testid="upload-notice">
          <p className="text-white font-bold uppercase text-sm sm:text-lg tracking-wide leading-relaxed">
            UPLOAD YOUR TV PROGRAM AT LEAST 7 DAYS BEFORE ITS SCHEDULED AIR DATE ON THE NOW TELEVISION NETWORK.
          </p>
          <p className="text-zinc-300 mt-2 sm:mt-3 text-xs sm:text-base">
            <strong>Example:</strong> If your show airs on March 7, you must upload it by March 1. 
            Uploads received later may miss that air date since scheduling is finalized 7 days in advance.
          </p>
        </div>

        {/* File Naming Instructions */}
        <div className="mb-6 sm:mb-8 p-4 sm:p-6 rounded-xl sm:rounded-2xl border" style={{ backgroundColor: `${primaryColor}15`, borderColor: primaryColor }} data-testid="naming-instructions">
          <h3 className="text-white font-bold uppercase text-sm sm:text-lg tracking-wide mb-3 sm:mb-4" style={{ color: primaryColor }}>
            FILE NAMING REQUIREMENTS
          </h3>
          <p className="text-zinc-300 mb-3 text-xs sm:text-base">
            To ensure your broadcast is scheduled and aired on time, please name your video file using the exact format below:
          </p>
          <div className="bg-zinc-900/50 p-3 sm:p-4 rounded-lg mb-3 sm:mb-4">
            <p className="text-white font-mono text-xs sm:text-sm mb-2">
              <strong>Format:</strong> Day of Week – Month – Air Date – Air Time – First and Last Name
            </p>
            <p className="text-zinc-400 font-mono text-xs sm:text-sm">
              <strong>Example:</strong> Monday June 5th at 6.30 PM – Pastor John Doe
            </p>
          </div>
          <div className="space-y-2">
            <p className="text-red-400 font-semibold text-xs sm:text-sm">
              ⚠️ Important: Files that are not labeled correctly will be rejected.
            </p>
            <p className="text-red-400 font-semibold text-xs sm:text-sm">
              ⚠️ Runtime Requirements: Programs must be 28:30 (28 minutes, 30 seconds) for a 30-minute slot or 58:30 (58 minutes, 30 seconds) for a one-hour slot. If our production team is editing your program, you may submit it at any length and we will edit it to the correct broadcast time. Programs not meeting the 28:30 or 58:30 runtime (and not scheduled for editing) will be rejected.
            </p>
          </div>
        </div>

        {/* Upload Zone */}
        <div
          className={`relative border-2 border-dashed rounded-xl transition-all duration-300 ${
            isDragging 
              ? 'border-red-500 bg-red-500/5 dropzone-active' 
              : 'border-zinc-700 hover:border-zinc-600 bg-zinc-900/30'
          }`}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          data-testid="upload-dropzone"
        >
          <input
            type="file"
            ref={fileInputRef}
            onChange={handleFileSelect}
            multiple
            className="hidden"
            data-testid="file-input"
          />
          <input
            type="file"
            ref={resumeFileInputRef}
            onChange={handleResumeFileSelect}
            className="hidden"
            data-testid="resume-file-input"
          />
          
          <div className="flex flex-col items-center justify-center py-12 sm:py-20 px-4 sm:px-6">
            <div className={`w-16 h-16 sm:w-20 sm:h-20 rounded-xl mb-4 sm:mb-6 flex items-center justify-center transition-all duration-300 ${
              isDragging ? 'bg-red-600 scale-110' : 'bg-zinc-800'
            }`}>
              <Upload className={`w-8 h-8 sm:w-10 sm:h-10 ${isDragging ? 'text-white' : 'text-zinc-400'}`} />
            </div>
            
            <h2 className="font-display text-xl sm:text-3xl text-white mb-2 tracking-tight text-center">
              {isDragging ? 'DROP FILES HERE' : 'DRAG & DROP FILES'}
            </h2>
            <p className="text-zinc-500 mb-4 sm:mb-6 text-center max-w-md text-sm sm:text-base">
              Drop your video files here or click to browse. Supports files up to {user?.max_file_size_gb || 10}GB with automatic resume.
            </p>
            
            <Button
              onClick={() => fileInputRef.current?.click()}
              className="rounded-lg text-white uppercase tracking-wider btn-glow text-sm sm:text-base px-6 py-2 sm:px-8 sm:py-3"
              style={{ backgroundColor: primaryColor }}
              data-testid="select-files-btn"
            >
              Select Files
            </Button>
          </div>
        </div>

        {/* Resume Incomplete Uploads */}
        {incompleteUploads.length > 0 && (
          <div className="mt-6 animate-fadeIn" data-testid="incomplete-uploads">
            <h3 className="font-display text-xl text-white mb-4 tracking-tight flex items-center gap-2">
              <RefreshCw className="w-5 h-5" style={{ color: primaryColor }} />
              RESUME UPLOADS
            </h3>
            <div className="space-y-3">
              {incompleteUploads.map(item => (
                <div
                  key={item.id}
                  className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-4"
                  data-testid={`incomplete-${item.id}`}
                >
                  <div className="flex items-center justify-between mb-3">
                    <div className="min-w-0 flex-1">
                      <p className="text-white font-medium truncate font-mono text-sm">{item.filename}</p>
                      <p className="text-zinc-500 text-xs">
                        {formatBytes(item.file_size)} • {item.received_chunks}/{item.total_chunks} chunks uploaded • {item.progress}% complete
                      </p>
                    </div>
                    <div className="flex items-center gap-2 ml-4">
                      <Button
                        size="sm"
                        onClick={() => handleResumeClick(item)}
                        className="text-white text-xs rounded-lg"
                        style={{ backgroundColor: primaryColor }}
                        data-testid={`resume-btn-${item.id}`}
                      >
                        <RefreshCw className="w-3 h-3 mr-1" />
                        Resume
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => cancelIncomplete(item.id)}
                        className="text-zinc-400 hover:text-red-400 text-xs"
                        data-testid={`cancel-incomplete-${item.id}`}
                      >
                        <X className="w-3 h-3" />
                      </Button>
                    </div>
                  </div>
                  <div className="w-full bg-zinc-800 rounded-full h-2">
                    <div
                      className="h-2 rounded-full transition-all duration-300"
                      style={{ width: `${item.progress}%`, backgroundColor: primaryColor }}
                    />
                  </div>
                  <p className="text-zinc-500 text-xs mt-2">
                    Click Resume, then select the original file from your computer to continue uploading.
                  </p>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Upload Queue */}
        {uploadQueue.length > 0 && (
          <div className="mt-8 animate-fadeIn" data-testid="upload-queue">
            <h3 className="font-display text-xl text-white mb-4 tracking-tight">UPLOAD QUEUE</h3>
            <div className="space-y-3">
              {uploadQueue.map(item => (
                <div
                  key={item.id}
                  className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-4"
                  data-testid={`queue-item-${item.id}`}
                >
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-3 min-w-0">
                      {getFileIcon(item.name)}
                      <div className="min-w-0">
                        <p className="text-white font-medium truncate font-mono text-sm">{item.name}</p>
                        <p className="text-zinc-500 text-xs">
                          {formatBytes(item.size)} • {item.uploadedChunks}/{item.totalChunks} chunks
                        </p>
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-2">
                      {item.status === 'uploading' && (
                        <>
                          <span className="text-zinc-400 text-xs mr-2">
                            {formatBytes(item.speed)}/s • ETA {formatDuration(item.eta)}
                          </span>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => pauseUpload(item.id)}
                            className="h-8 w-8 text-zinc-400 hover:text-white"
                          >
                            <Pause className="w-4 h-4" />
                          </Button>
                        </>
                      )}
                      
                      {item.status === 'paused' && (
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => resumeUpload(item)}
                          className="h-8 w-8 text-zinc-400 hover:text-white"
                        >
                          <Play className="w-4 h-4" />
                        </Button>
                      )}
                      
                      {item.status === 'completed' && (
                        <Check className="w-5 h-5 text-green-500" />
                      )}

                      {item.status === 'processing' && (
                        <Loader2 className="w-5 h-5 text-blue-400 animate-spin" />
                      )}
                      
                      {item.status === 'queued' && (
                        <span className="text-xs text-yellow-400 font-medium px-2 py-1 bg-yellow-500/10 rounded">QUEUED</span>
                      )}
                      
                      {item.status === 'failed' && (
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => resumeUpload(item)}
                          className="h-8 w-8 text-red-400 hover:text-red-300"
                        >
                          <RefreshCw className="w-4 h-4" />
                        </Button>
                      )}
                      
                      {item.status !== 'completed' && item.status !== 'processing' && (
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => cancelUpload(item.id)}
                          className="h-8 w-8 text-zinc-400 hover:text-red-400"
                        >
                          <X className="w-4 h-4" />
                        </Button>
                      )}
                    </div>
                  </div>
                  
                  <div className="relative">
                    <Progress 
                      value={item.progress} 
                      className="h-2 bg-zinc-800"
                    />
                    {item.status === 'uploading' && (
                      <div 
                        className="absolute top-0 left-0 h-2 bg-red-600 progress-glow transition-all duration-300"
                        style={{ width: `${item.progress}%` }}
                      />
                    )}
                    {item.status === 'processing' && (
                      <div 
                        className="absolute top-0 left-0 h-2 rounded-full transition-all duration-300"
                        style={{ width: '100%', backgroundColor: item.processingStatus === 'converting' ? '#eab308' : '#3b82f6', animation: 'pulse 2s ease-in-out infinite' }}
                      />
                    )}
                  </div>
                  
                  <div className="flex items-center justify-between mt-2">
                    <span className="text-xs text-zinc-500">
                      {item.status === 'processing' 
                        ? (item.processingMessage || 'Processing video...')
                        : `${item.progress}%`}
                    </span>
                    {getStatusBadge(item.status, item.processingStatus)}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Uploaded Files */}
        <div className="mt-12" data-testid="uploads-list">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-display text-xl text-white tracking-tight">YOUR UPLOADS</h3>
            <Button
              variant="ghost"
              size="sm"
              onClick={fetchUploads}
              className="text-zinc-400 hover:text-white"
              data-testid="refresh-uploads-btn"
            >
              <RefreshCw className="w-4 h-4 mr-2" />
              Refresh
            </Button>
          </div>
          
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <div className="w-6 h-6 border-2 border-red-600 border-t-transparent rounded-full animate-spin" />
            </div>
          ) : uploads.length === 0 ? (
            <div className="text-center py-12 bg-zinc-900/30 border border-zinc-800 rounded-xl">
              <HardDrive className="w-12 h-12 text-zinc-700 mx-auto mb-4" />
              <p className="text-zinc-500">No files uploaded yet</p>
              <p className="text-zinc-600 text-sm mt-1">Your uploaded files will appear here</p>
            </div>
          ) : (
            <ScrollArea className="h-[400px]">
              <div className="space-y-2">
                {uploads.map(upload => (
                  <div
                    key={upload.id}
                    className={`file-item flex items-center justify-between p-4 bg-zinc-900/50 border rounded-xl ${
                      upload.processing_status === 'rejected' ? 'border-red-800/50' : 'border-zinc-800'
                    }`}
                    data-testid={`upload-item-${upload.id}`}
                  >
                    <div className="flex items-center gap-3 min-w-0">
                      {getFileIcon(upload.filename)}
                      <div className="min-w-0">
                        <p className="text-white font-mono text-sm truncate">
                          {upload.filename}
                          {upload.converted && (
                            <span className="ml-2 text-xs text-green-400">(converted)</span>
                          )}
                        </p>
                        <div className="flex items-center gap-3 text-xs text-zinc-500">
                          <span>{formatBytes(upload.size_bytes)}</span>
                          <span className="flex items-center gap-1">
                            <Clock className="w-3 h-3" />
                            {formatDate(upload.completed_at || upload.created_at)}
                          </span>
                          {upload.video_info && (
                            <span className="text-zinc-600">
                              {upload.video_info.resolution} • {upload.video_info.video_codec?.toUpperCase()}
                            </span>
                          )}
                        </div>
                        {upload.processing_status === 'rejected' && upload.rejection_reason && (
                          <p className="mt-2 text-sm text-red-400 font-medium">
                            {upload.rejection_reason}
                          </p>
                        )}
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-2 sm:gap-3">
                      {upload.processing_status && getProcessingBadge(upload.processing_status, upload.processing_message)}
                      {user?.can_delete_files && (
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => deleteUpload(upload.id)}
                          className="h-8 w-8 text-zinc-500 hover:text-red-400"
                          data-testid={`delete-upload-${upload.id}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </ScrollArea>
          )}
        </div>

        {/* User Info */}
        <div className="mt-8 p-4 bg-zinc-900/30 border border-zinc-800 rounded-xl">
          <div className="flex items-center gap-6 text-sm">
            <div>
              <span className="text-zinc-500">Storage Used:</span>
              <span className="text-white ml-2 font-mono">{formatBytes(user?.total_size_uploaded || 0)}</span>
            </div>
            <div>
              <span className="text-zinc-500">Total Uploads:</span>
              <span className="text-white ml-2 font-mono">{user?.total_uploads || 0}</span>
            </div>
            {user?.storage_quota_gb && (
              <div>
                <span className="text-zinc-500">Quota:</span>
                <span className="text-white ml-2 font-mono">{user.storage_quota_gb} GB</span>
              </div>
            )}
          </div>
        </div>
      </main>

      {/* Change Password Dialog */}
      {showPasswordDialog && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60" data-testid="change-password-dialog">
          <div className="bg-zinc-900 border border-zinc-700 rounded-xl p-6 w-full max-w-sm mx-4">
            <h3 className="text-lg font-semibold text-white mb-4">Change Password</h3>
            <div className="space-y-3">
              <input
                type="password"
                placeholder="Current Password"
                value={passwordForm.current_password}
                onChange={(e) => setPasswordForm(prev => ({ ...prev, current_password: e.target.value }))}
                className="w-full px-3 py-2 bg-zinc-800 border border-zinc-700 rounded-lg text-white text-sm focus:outline-none focus:border-zinc-500"
                data-testid="current-password-input"
              />
              <input
                type="password"
                placeholder="New Password"
                value={passwordForm.new_password}
                onChange={(e) => setPasswordForm(prev => ({ ...prev, new_password: e.target.value }))}
                className="w-full px-3 py-2 bg-zinc-800 border border-zinc-700 rounded-lg text-white text-sm focus:outline-none focus:border-zinc-500"
                data-testid="new-password-input"
              />
              <input
                type="password"
                placeholder="Confirm New Password"
                value={passwordForm.confirm_password}
                onChange={(e) => setPasswordForm(prev => ({ ...prev, confirm_password: e.target.value }))}
                className="w-full px-3 py-2 bg-zinc-800 border border-zinc-700 rounded-lg text-white text-sm focus:outline-none focus:border-zinc-500"
                data-testid="confirm-password-input"
              />
            </div>
            <div className="flex gap-2 mt-4">
              <Button
                variant="outline"
                size="sm"
                onClick={() => { setShowPasswordDialog(false); setPasswordForm({ current_password: '', new_password: '', confirm_password: '' }); }}
                className="flex-1 border-zinc-700 text-zinc-400 hover:text-white"
                data-testid="cancel-password-btn"
              >
                Cancel
              </Button>
              <Button
                size="sm"
                onClick={handleChangePassword}
                className="flex-1 bg-white text-black hover:bg-zinc-200"
                data-testid="save-password-btn"
              >
                Save Password
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UploadPortal;